import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Avatar, Box, Divider, Typography } from '@mui/material';
import { alpha, type Theme } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import MenuIcon from '@mui/icons-material/Menu';
import LogoutIcon from '@mui/icons-material/Logout';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { Icon } from '@malash-office/ui-components';
import { useAppContext } from '@/context/AppContext';
import { LOGOUT_PATH, NAV_ITEMS, NavItem } from './navConfig';
import { MOBILE_NAV_BOTTOM_GAP, MOBILE_NAV_HEIGHT } from './mobileLayoutConstants';
import chabadLogo from '@/assets/chabad-logo.png';

const normalizePath = (path: string) => {
  const p = path.endsWith('/') ? path.slice(0, -1).toLowerCase() : path.toLowerCase();
  return p || '/';
};

/** Bottom nav pill + full-screen menu overlay. Rendered only on small screens. */
const MobileNav = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, hasPermission } = useAppContext();
  const [menuOpen, setMenuOpen] = useState(false);
  // When set, the overlay shows this parent item's children as a drill-down sub-menu.
  const [submenu, setSubmenu] = useState<NavItem | null>(null);

  const selected = normalizePath(location.pathname);

  const isItemVisible = (item: NavItem): boolean => {
    if (item.children) {
      return item.children.some(c => !c.permission || hasPermission(c.permission));
    }
    return !item.permission || hasPermission(item.permission);
  };

  const isItemSelected = (item: NavItem): boolean => {
    if (item.to) return selected === normalizePath(item.to);
    return !!item.children?.some(c => selected === normalizePath(c.to));
  };

  const visibleItems = NAV_ITEMS.filter(isItemVisible);
  // Quick links in the bottom pill: leaf items only (parents open in the overlay).
  const quickItems = visibleItems.filter(item => !item.children).slice(0, 4);

  const closeMenu = () => {
    setMenuOpen(false);
    setSubmenu(null);
  };

  const onLogout = () => {
    closeMenu();
    logout();
    navigate('/login');
  };

  const go = (to: string) => {
    if (to === LOGOUT_PATH) return onLogout();
    closeMenu();
    navigate(to);
  };

  const pillItemSx = (active: boolean) => ({
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 0.5,
    cursor: 'pointer',
    color: active ? 'primary.main' : 'text.secondary',
    minWidth: 0
  });

  const pillIconTileSx = (active: boolean) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 40,
    height: 32,
    borderRadius: '10px',
    backgroundColor: (theme: Theme) => (active ? alpha(theme.palette.primary.main, 0.12) : 'transparent')
  });

  // Shared big menu card (used for top-level items and for children inside a sub-menu).
  const renderMenuCard = (opts: {
    key: string;
    icon: NavItem['icon'];
    label: string;
    active: boolean;
    onClick: () => void;
    trailing?: 'drill' | 'none';
  }) => (
    <Box
      key={opts.key}
      onClick={opts.onClick}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        minHeight: 72,
        px: 2,
        borderRadius: '20px',
        cursor: 'pointer',
        backgroundColor: alpha('#fff', opts.active ? 0.28 : 0.18),
        transition: 'background-color 0.2s'
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 48,
          height: 48,
          flexShrink: 0,
          borderRadius: '12px',
          backgroundColor: alpha('#fff', 0.22)
        }}
      >
        <Icon icon={opts.icon} size="medium" color="common.white" />
      </Box>
      <Typography variant="h6" fontWeight={700} sx={{ flex: 1, minWidth: 0 }} noWrap>
        {opts.label}
      </Typography>
      {opts.trailing === 'drill' && <ChevronLeftIcon />}
    </Box>
  );

  return (
    <>
      {/* Bottom nav pill */}
      <Box
        sx={{
          position: 'fixed',
          bottom: MOBILE_NAV_BOTTOM_GAP,
          left: 12,
          right: 12,
          height: MOBILE_NAV_HEIGHT,
          display: 'flex',
          alignItems: 'center',
          px: 1,
          borderRadius: '24px',
          backgroundColor: theme => alpha(theme.palette.common.white, 0.92),
          backdropFilter: 'blur(12px)',
          boxShadow: '0 4px 30px rgba(98, 112, 131, 0.25)',
          zIndex: 1200
        }}
      >
        {/* תפריט (rightmost in RTL) */}
        <Box onClick={() => (menuOpen ? closeMenu() : setMenuOpen(true))} sx={pillItemSx(menuOpen)}>
          <Box sx={pillIconTileSx(menuOpen)}>
            <MenuIcon sx={{ fontSize: 22 }} />
          </Box>
          <Typography variant="caption" fontWeight={600} noWrap>
            תפריט
          </Typography>
        </Box>

        {quickItems.map(item => {
          const active = isItemSelected(item);
          return (
            <Box key={item.label} onClick={() => item.to && go(item.to)} sx={pillItemSx(active)}>
              <Box sx={pillIconTileSx(active)}>
                <Icon icon={item.icon} size="small" color={active ? 'primary.main' : 'text.secondary'} />
              </Box>
              <Typography variant="caption" fontWeight={600} noWrap sx={{ maxWidth: '100%' }}>
                {item.label}
              </Typography>
            </Box>
          );
        })}
      </Box>

      {/* Full-screen menu overlay */}
      {menuOpen && (
        <Box
          sx={{
            position: 'fixed',
            inset: 0,
            zIndex: 1300,
            display: 'flex',
            flexDirection: 'column',
            color: 'common.white',
            background: theme =>
              `linear-gradient(180deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`
          }}
        >
          {submenu ? (
            /* ---- Sub-menu (drill-down) view ---- */
            <>
              {/* Sub-menu header: parent title + back, and close */}
              <Box
                sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 2, flexShrink: 0 }}
              >
                <Box
                  onClick={() => setSubmenu(null)}
                  sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer', minWidth: 0 }}
                >
                  <ChevronRightIcon />
                  <Typography variant="h6" fontWeight={700} noWrap>
                    {submenu.label}
                  </Typography>
                  <Icon icon={submenu.icon} size="small" color="common.white" />
                </Box>
                <CloseIcon sx={{ cursor: 'pointer', fontSize: 28 }} onClick={closeMenu} />
              </Box>

              {/* Children */}
              <Box sx={{ flex: 1, overflowY: 'auto', p: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {submenu.children
                  ?.filter(c => !c.permission || hasPermission(c.permission))
                  .map(child =>
                    renderMenuCard({
                      key: child.label,
                      icon: child.icon,
                      label: child.label,
                      active: selected === normalizePath(child.to),
                      onClick: () => go(child.to),
                      trailing: 'none'
                    })
                  )}
              </Box>
            </>
          ) : (
            /* ---- Main menu view ---- */
            <>
              {/* Header */}
              <Box
                sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 2, flexShrink: 0 }}
              >
                <Typography variant="h6" fontWeight={700}>
                  תפריט
                </Typography>
                <CloseIcon sx={{ cursor: 'pointer', fontSize: 28 }} onClick={closeMenu} />
              </Box>

              {/* Profile */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 2, pb: 2, flexShrink: 0 }}>
                <Avatar sx={{ width: 48, height: 48, bgcolor: 'common.white', color: 'primary.main', fontWeight: 700 }}>
                  {user?.firstName?.[0] || 'מ'}
                </Avatar>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography fontWeight={700} noWrap>
                    {user?.firstName} {user?.lastName}
                  </Typography>
                  {!!user?.roleNames.length && (
                    <Typography variant="caption" sx={{ opacity: 0.85 }} noWrap>
                      {user.roleNames.join(', ')}
                    </Typography>
                  )}
                </Box>
                <Box component="img" src={chabadLogo} alt="חב״ד" sx={{ width: 56 }} />
              </Box>

              <Divider sx={{ borderColor: alpha('#fff', 0.25), mx: 2, flexShrink: 0 }} />

              {/* Items */}
              <Box sx={{ flex: 1, overflowY: 'auto', p: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {visibleItems.map(item => {
                  const hasChildren = !!item.children?.some(c => !c.permission || hasPermission(c.permission));
                  return renderMenuCard({
                    key: item.label,
                    icon: item.icon,
                    label: item.label,
                    active: isItemSelected(item),
                    onClick: () => (hasChildren ? setSubmenu(item) : item.to && go(item.to)),
                    trailing: hasChildren ? 'drill' : 'none'
                  });
                })}
              </Box>

              {/* Logout */}
              <Box sx={{ p: 2, flexShrink: 0 }}>
                <Box
                  onClick={onLogout}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 1,
                    minHeight: 52,
                    borderRadius: '16px',
                    cursor: 'pointer',
                    border: `1px solid ${alpha('#fff', 0.5)}`
                  }}
                >
                  <LogoutIcon />
                  <Typography fontWeight={700}>התנתקות</Typography>
                </Box>
              </Box>
            </>
          )}
        </Box>
      )}
    </>
  );
};

export default MobileNav;
